import {
	ActionIcon,
	Button,
	type MantineColorsTuple,
	type MantineThemeOverride,
	Text
} from '@mantine/core';
import { toMerged } from 'es-toolkit';

type CmsTheme = MantineThemeOverride & {
	primary?: readonly string[] | null;
	secondary?: readonly string[] | null;
	primaryShade?: number | MantineThemeOverride['primaryShade'];
	ui?: unknown;
};

const isMantinePalette = (
	palette: readonly string[] | null | undefined
): palette is MantineColorsTuple => {
	return Array.isArray(palette) && palette.length === 10;
};

const getComponentOverrides = (
	components?: CmsTheme['components'] | null
): MantineThemeOverride['components'] | undefined => {
	if (!components) {
		return undefined;
	}

	const componentOverrides: NonNullable<MantineThemeOverride['components']> =
		{};

	if (components.Button) {
		componentOverrides.Button = Button.extend(components.Button);
	}

	if (components.Text) {
		componentOverrides.Text = Text.extend(components.Text);
	}

	if (components.ActionIcon) {
		componentOverrides.ActionIcon = ActionIcon.extend(components.ActionIcon);
	}

	return Object.keys(componentOverrides).length > 0
		? componentOverrides
		: undefined;
};

export const createGetTheme =
	(defaultTheme: MantineThemeOverride) =>
	(cmsTheme?: CmsTheme | null): MantineThemeOverride => {
		const override: MantineThemeOverride = {};

		if (cmsTheme?.primaryColor) {
			override.primaryColor = cmsTheme.primaryColor;
		}

		if (isMantinePalette(cmsTheme?.primary)) {
			override.colors = {
				...override.colors,
				primary: cmsTheme.primary
			};
			override.primaryColor ??= 'primary';
		}

		if (isMantinePalette(cmsTheme?.secondary)) {
			override.colors = {
				...override.colors,
				secondary: cmsTheme.secondary
			};
		}

		const componentOverrides = getComponentOverrides(cmsTheme?.components);

		if (componentOverrides) {
			override.components = componentOverrides;
		}

		const theme = toMerged(defaultTheme, override);

		if (override.colors) {
			return {
				...theme,
				colors: {
					...defaultTheme.colors,
					...override.colors
				}
			};
		}

		return theme;
	};

export type { CmsTheme };
